import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Form,
  Input,
  List,
  Modal,
  Progress,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs, { Dayjs } from "dayjs";
import type { ColumnsType } from "antd/es/table";
import type { BadgeProps } from "antd";
import API_CONFIG from "../../config/api";
import { useSelection } from "../../SelectionContext";
import { useAuth } from "../../context/AuthContext";

const { RangePicker } = DatePicker;
const { Text } = Typography;

type RoomStatus =
  | "available"
  | "occupied"
  | "out_of_order"
  | "out_of_service"
  | "maintenance";
type HousekeepingStatus =
  | "clean"
  | "dirty"
  | "cleaning"
  | "inspected"
  | "out_of_service";

type HotelProperty = {
  id: number;
  name: string;
};

type HotelRoomType = {
  id: number;
  name: string;
  property_id: number;
};

type ApiRoom = {
  id: number;
  room_number: string;
  room_type_id: number;
  room_type_name?: string | null;
  building_name?: string | null;
  floor_name?: string | null;
  status: RoomStatus;
  housekeeping_status: HousekeepingStatus;
  property_id: number;
  property_name?: string | null;
};

type ApiRoomBlock = {
  id: number;
  room_number: string;
  start_date: string;
  end_date: string;
  reason?: string | null;
  status: string;
  property_name?: string | null;
};

type RatePlan = {
  id: number;
  name: string;
  property_id: number;
  currency_code?: string | null;
};

type ApiRateCalendarEntry = {
  id: number;
  property_id: number;
  rate_plan_id: number;
  room_type_id: number;
  room_type_name?: string | null;
  rate_date: string;
  price: number | string;
  min_stay?: number | null;
  max_stay?: number | null;
  stop_sell?: number | boolean | null;
  close_to_arrival?: number | boolean | null;
  close_to_departure?: number | boolean | null;
};

type RoomRecord = {
  id: number;
  roomNumber: string;
  roomType: string;
  building?: string;
  floor?: string;
  status: RoomStatus;
  housekeepingStatus: HousekeepingStatus;
  propertyId: number;
  propertyName: string;
};

type RoomBlock = {
  id: number;
  roomNumber: string;
  startDate: string;
  endDate: string;
  reason?: string;
  status: string;
  propertyName: string;
};

type RateCalendarEntry = {
  id: number;
  propertyId: number;
  ratePlanId: number;
  roomTypeId: number;
  roomTypeName: string;
  rateDate: string;
  price: number;
  minStay?: number | null;
  maxStay?: number | null;
  stopSell: boolean;
  closeToArrival: boolean;
  closeToDeparture: boolean;
};

type RoomFilters = {
  search: string;
  status: RoomStatus | "all";
  housekeeping: HousekeepingStatus | "all";
  roomTypeId: number | "all";
  propertyId: number | "all";
};

type CalendarFilters = {
  propertyId: number | "all";
  roomTypeId: number | "all";
  ratePlanId: number | "all";
  dateRange: [Dayjs, Dayjs] | null;
};

type RoomFormValues = {
  propertyId: number;
  roomTypeId: number;
  roomNumber: string;
  name?: string;
  status: RoomStatus;
  housekeepingStatus: HousekeepingStatus;
  isActive: boolean;
  notes?: string;
};

const statusLabels: Record<RoomStatus, string> = {
  available: "Disponible",
  occupied: "Occupee",
  out_of_order: "Hors service",
  out_of_service: "Hors service",
  maintenance: "Maintenance",
};

const statusColors: Record<RoomStatus, string> = {
  available: "green",
  occupied: "blue",
  out_of_order: "orange",
  out_of_service: "red",
  maintenance: "gold",
};

const housekeepingLabels: Record<HousekeepingStatus, string> = {
  clean: "Propre",
  dirty: "Sale",
  cleaning: "En cours",
  inspected: "Inspectee",
  out_of_service: "Hors service",
};

const housekeepingBadgeStatus: Record<HousekeepingStatus, BadgeProps["status"]> =
  {
    clean: "success",
    dirty: "error",
    cleaning: "processing",
    inspected: "default",
    out_of_service: "warning",
  };

const RoomsAvailabilityTab: React.FC = () => {
  const { selectedCompany } = useSelection();
  const { user, hasPermission } = useAuth();
  const [rooms, setRooms] = useState<RoomRecord[]>([]);
  const [calendarRoomBlocks, setCalendarRoomBlocks] = useState<RoomBlock[]>([]);
  const [properties, setProperties] = useState<HotelProperty[]>([]);
  const [roomTypes, setRoomTypes] = useState<HotelRoomType[]>([]);
  const [calendarRoomTypes, setCalendarRoomTypes] = useState<HotelRoomType[]>([]);
  const [ratePlans, setRatePlans] = useState<RatePlan[]>([]);
  const [rateCalendarEntries, setRateCalendarEntries] = useState<
    RateCalendarEntry[]
  >([]);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [loadingCalendarBlocks, setLoadingCalendarBlocks] = useState(false);
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [updatingRooms, setUpdatingRooms] = useState<Record<number, boolean>>({});
  const [roomModalOpen, setRoomModalOpen] = useState(false);
  const [creatingRoom, setCreatingRoom] = useState(false);
  const [roomForm] = Form.useForm<RoomFormValues>();
  const [filters, setFilters] = useState<RoomFilters>({
    search: "",
    status: "all",
    housekeeping: "all",
    roomTypeId: "all",
    propertyId: "all",
  });
  const [calendarFilters, setCalendarFilters] = useState<CalendarFilters>({
    propertyId: "all",
    roomTypeId: "all",
    ratePlanId: "all",
    dateRange: [dayjs(), dayjs().add(14, "day")],
  });

  const requiresCompanySelection = Boolean(user?.is_superadmin && !selectedCompany);
  const hasProperties = properties.length > 0;
  const hasRoomTypes = roomTypes.length > 0;
  const canCreateRooms = Boolean(
    hasPermission?.("Hotellerie.Configuration.Chambres.create")
  );
  const canEditRooms = Boolean(
    hasPermission?.("Hotellerie.Configuration.Chambres.edit")
  );
  const formPropertyId = Form.useWatch("propertyId", roomForm);

  const availableRoomTypes = useMemo(() => {
    if (!formPropertyId) {
      return roomTypes;
    }
    return roomTypes.filter(
      (roomType) => roomType.property_id === formPropertyId
    );
  }, [formPropertyId, roomTypes]);

  const ratePlanMap = useMemo(() => {
    const map = new Map<number, RatePlan>();
    ratePlans.forEach((plan) => map.set(plan.id, plan));
    return map;
  }, [ratePlans]);

  const formatCalendarPrice = useCallback(
    (value: number, ratePlanId: number) => {
      const formatted = new Intl.NumberFormat("fr-FR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(Number(value || 0));
      const plan = ratePlanMap.get(ratePlanId);
      const suffix = plan?.currency_code;
      return suffix ? `${formatted} ${suffix}` : formatted;
    },
    [ratePlanMap]
  );

  const fetchProperties = useCallback(async () => {
    if (requiresCompanySelection) {
      return;
    }
    try {
      const params = selectedCompany ? { company_id: selectedCompany } : {};
      const response = await axios.get(`${API_CONFIG.API_URL}/hotel/properties`, {
        params,
      });
      if (response.data.success) {
        setProperties(response.data.data || []);
      }
    } catch (error: any) {
      console.error("[HOTEL] Error fetching properties:", error);
      message.error(
        error.response?.data?.message || "Erreur lors du chargement des proprietes"
      );
    }
  }, [requiresCompanySelection, selectedCompany]);

  const fetchRoomTypes = useCallback(async () => {
    if (requiresCompanySelection) {
      return;
    }
    try {
      const params: Record<string, string | number> = {};
      if (selectedCompany) {
        params.company_id = selectedCompany;
      }
      if (filters.propertyId !== "all") {
        params.property_id = filters.propertyId;
      }
      const response = await axios.get(`${API_CONFIG.API_URL}/hotel/room-types`, {
        params,
      });
      if (response.data.success) {
        setRoomTypes(response.data.data || []);
      }
    } catch (error: any) {
      console.error("[HOTEL] Error fetching room types:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors du chargement des types de chambres"
      );
    }
  }, [filters.propertyId, requiresCompanySelection, selectedCompany]);

  const fetchCalendarRoomTypes = useCallback(async () => {
    if (requiresCompanySelection) {
      return;
    }
    try {
      const params: Record<string, string | number> = {};
      if (selectedCompany) {
        params.company_id = selectedCompany;
      }
      if (calendarFilters.propertyId !== "all") {
        params.property_id = calendarFilters.propertyId;
      }
      const response = await axios.get(`${API_CONFIG.API_URL}/hotel/room-types`, {
        params,
      });
      if (response.data.success) {
        setCalendarRoomTypes(response.data.data || []);
      }
    } catch (error: any) {
      console.error("[HOTEL] Error fetching calendar room types:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors du chargement des types de chambres"
      );
    }
  }, [calendarFilters.propertyId, requiresCompanySelection, selectedCompany]);

  const fetchRatePlans = useCallback(async () => {
    if (requiresCompanySelection) {
      return;
    }
    try {
      const params: Record<string, string | number> = {};
      if (selectedCompany) {
        params.company_id = selectedCompany;
      }
      if (calendarFilters.propertyId !== "all") {
        params.property_id = calendarFilters.propertyId;
      }
      const response = await axios.get(`${API_CONFIG.API_URL}/hotel/rate-plans`, {
        params,
      });
      if (response.data.success) {
        setRatePlans(response.data.data || []);
      }
    } catch (error: any) {
      console.error("[HOTEL] Error fetching rate plans:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors du chargement des plans tarifaires"
      );
    }
  }, [calendarFilters.propertyId, requiresCompanySelection, selectedCompany]);

  const fetchRooms = useCallback(async () => {
    if (requiresCompanySelection) {
      return;
    }
    setLoadingRooms(true);
    try {
      const params: Record<string, string | number> = {};
      if (selectedCompany) {
        params.company_id = selectedCompany;
      }
      if (filters.propertyId !== "all") {
        params.property_id = filters.propertyId;
      }
      if (filters.roomTypeId !== "all") {
        params.room_type_id = filters.roomTypeId;
      }
      if (filters.status !== "all") {
        params.status = filters.status;
      }
      if (filters.housekeeping !== "all") {
        params.housekeeping_status = filters.housekeeping;
      }

      const response = await axios.get(`${API_CONFIG.API_URL}/hotel/rooms`, {
        params,
      });
      if (response.data.success) {
        const mapped = (response.data.data || []).map((row: ApiRoom) => ({
          id: row.id,
          roomNumber: row.room_number,
          roomType: row.room_type_name || `Type #${row.room_type_id}`,
          building: row.building_name || undefined,
          floor: row.floor_name || undefined,
          status: row.status,
          housekeepingStatus: row.housekeeping_status,
          propertyId: Number(row.property_id),
          propertyName: row.property_name || `Propriete #${row.property_id}`,
        }));
        setRooms(mapped);
      }
    } catch (error: any) {
      console.error("[HOTEL] Error fetching rooms:", error);
      message.error(
        error.response?.data?.message || "Erreur lors du chargement des chambres"
      );
    } finally {
      setLoadingRooms(false);
    }
  }, [filters.housekeeping, filters.propertyId, filters.roomTypeId, filters.status, requiresCompanySelection, selectedCompany]);

  const fetchRateCalendar = useCallback(async () => {
    if (requiresCompanySelection) {
      return;
    }
    setLoadingCalendar(true);
    try {
      const params: Record<string, string | number> = {};
      if (selectedCompany) {
        params.company_id = selectedCompany;
      }
      if (calendarFilters.propertyId !== "all") {
        params.property_id = calendarFilters.propertyId;
      }
      if (calendarFilters.roomTypeId !== "all") {
        params.room_type_id = calendarFilters.roomTypeId;
      }
      if (calendarFilters.ratePlanId !== "all") {
        params.rate_plan_id = calendarFilters.ratePlanId;
      }
      if (calendarFilters.dateRange) {
        params.start_date = calendarFilters.dateRange[0].format("YYYY-MM-DD");
        params.end_date = calendarFilters.dateRange[1].format("YYYY-MM-DD");
      }

      const response = await axios.get(`${API_CONFIG.API_URL}/hotel/rate-calendar`, {
        params,
      });
      if (response.data.success) {
        const mapped = (response.data.data || []).map(
          (row: ApiRateCalendarEntry) => ({
            id: row.id,
            propertyId: Number(row.property_id),
            ratePlanId: Number(row.rate_plan_id),
            roomTypeId: Number(row.room_type_id),
            roomTypeName: row.room_type_name || `Type #${row.room_type_id}`,
            rateDate: row.rate_date,
            price: Number(row.price || 0),
            minStay: row.min_stay ?? null,
            maxStay: row.max_stay ?? null,
            stopSell: Boolean(row.stop_sell),
            closeToArrival: Boolean(row.close_to_arrival),
            closeToDeparture: Boolean(row.close_to_departure),
          })
        );
        setRateCalendarEntries(mapped);
      }
    } catch (error: any) {
      console.error("[HOTEL] Error fetching rate calendar:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors du chargement du calendrier"
      );
    } finally {
      setLoadingCalendar(false);
    }
  }, [calendarFilters, requiresCompanySelection, selectedCompany]);

  const fetchCalendarRoomBlocks = useCallback(async () => {
    if (requiresCompanySelection) {
      return;
    }
    setLoadingCalendarBlocks(true);
    try {
      const params: Record<string, string | number> = {};
      if (selectedCompany) {
        params.company_id = selectedCompany;
      }
      if (calendarFilters.propertyId !== "all") {
        params.property_id = calendarFilters.propertyId;
      }
      if (calendarFilters.dateRange) {
        params.start_date = calendarFilters.dateRange[0].format("YYYY-MM-DD");
        params.end_date = calendarFilters.dateRange[1].format("YYYY-MM-DD");
      }
      params.status = "blocked";

      const response = await axios.get(`${API_CONFIG.API_URL}/hotel/room-blocks`, {
        params,
      });
      if (response.data.success) {
        const mapped = (response.data.data || []).map((row: ApiRoomBlock) => ({
          id: row.id,
          roomNumber: row.room_number,
          startDate: row.start_date,
          endDate: row.end_date,
          reason: row.reason || undefined,
          status: row.status,
          propertyName: row.property_name || "Propriete",
        }));
        setCalendarRoomBlocks(mapped);
      }
    } catch (error: any) {
      console.error("[HOTEL] Error fetching room blocks:", error);
      message.error(
        error.response?.data?.message || "Erreur lors du chargement des blocages"
      );
    } finally {
      setLoadingCalendarBlocks(false);
    }
  }, [calendarFilters, requiresCompanySelection, selectedCompany]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  useEffect(() => {
    fetchRoomTypes();
  }, [fetchRoomTypes]);

  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  useEffect(() => {
    fetchCalendarRoomTypes();
  }, [fetchCalendarRoomTypes]);

  useEffect(() => {
    fetchRatePlans();
  }, [fetchRatePlans]);

  useEffect(() => {
    fetchRateCalendar();
  }, [fetchRateCalendar]);

  useEffect(() => {
    fetchCalendarRoomBlocks();
  }, [fetchCalendarRoomBlocks]);

  const filteredRooms = useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    return rooms.filter((room) => {
      const matchesSearch =
        !search ||
        room.roomNumber.toLowerCase().includes(search) ||
        room.roomType.toLowerCase().includes(search);
      return matchesSearch;
    });
  }, [filters.search, rooms]);

  const stats = useMemo(() => {
    const total = rooms.length;
    const available = rooms.filter((room) => room.status === "available").length;
    const occupied = rooms.filter((room) => room.status === "occupied").length;
    const outOfService = rooms.filter((room) =>
      ["out_of_service", "out_of_order", "maintenance"].includes(room.status)
    ).length;
    const dirty = rooms.filter((room) => room.housekeepingStatus === "dirty").length;
    const occupancyRate = total ? Math.round((occupied / total) * 100) : 0;

    return {
      total,
      available,
      occupied,
      outOfService,
      dirty,
      occupancyRate,
    };
  }, [rooms]);

  const availabilityByType = useMemo(() => {
    const map = new Map<
      string,
      { roomType: string; total: number; available: number; occupied: number; outOfService: number }
    >();
    rooms.forEach((room) => {
      const entry = map.get(room.roomType) || {
        roomType: room.roomType,
        total: 0,
        available: 0,
        occupied: 0,
        outOfService: 0,
      };
      entry.total += 1;
      if (room.status === "available") entry.available += 1;
      if (room.status === "occupied") entry.occupied += 1;
      if (["out_of_service", "out_of_order", "maintenance"].includes(room.status)) {
        entry.outOfService += 1;
      }
      map.set(room.roomType, entry);
    });
    return Array.from(map.values());
  }, [rooms]);

  const updateRoom = async (roomId: number, payload: Record<string, any>) => {
    if (requiresCompanySelection) {
      return;
    }
    setUpdatingRooms((prev) => ({ ...prev, [roomId]: true }));
    try {
      const statePatch: Partial<RoomRecord> = {};
      if (payload.status) {
        statePatch.status = payload.status as RoomStatus;
      }
      if (payload.housekeeping_status) {
        statePatch.housekeepingStatus =
          payload.housekeeping_status as HousekeepingStatus;
      }
      const response = await axios.put(
        `${API_CONFIG.API_URL}/hotel/rooms/${roomId}`,
        {
          ...(selectedCompany ? { company_id: selectedCompany } : {}),
          ...payload,
        }
      );
      if (response.data.success) {
        setRooms((prev) =>
          prev.map((room) =>
            room.id === roomId ? { ...room, ...statePatch } : room
          )
        );
      }
    } catch (error: any) {
      console.error("[HOTEL] Error updating room:", error);
      message.error(
        error.response?.data?.message || "Erreur lors de la mise a jour"
      );
    } finally {
      setUpdatingRooms((prev) => ({ ...prev, [roomId]: false }));
    }
  };

  const openCreateRoom = () => {
    if (!canCreateRooms) {
      message.warning("Vous n'avez pas les droits pour creer une chambre.");
      return;
    }
    if (!hasProperties) {
      message.warning("Creez d'abord une propriete.");
      return;
    }
    if (!hasRoomTypes) {
      message.warning("Creez d'abord un type de chambre.");
      return;
    }
    const defaultPropertyId =
      filters.propertyId !== "all" ? filters.propertyId : properties[0]?.id;
    roomForm.resetFields();
    roomForm.setFieldsValue({
      propertyId: defaultPropertyId,
      status: "available",
      housekeepingStatus: "clean",
      isActive: true,
    });
    setRoomModalOpen(true);
  };

  const handleSaveRoom = async () => {
    if (!canCreateRooms) {
      message.warning("Vous n'avez pas les droits pour creer une chambre.");
      return;
    }
    const values = await roomForm.validateFields();
    const payload = {
      ...(selectedCompany ? { company_id: selectedCompany } : {}),
      property_id: values.propertyId,
      room_type_id: values.roomTypeId,
      room_number: values.roomNumber,
      name: values.name || null,
      status: values.status,
      housekeeping_status: values.housekeepingStatus,
      is_active: values.isActive ? 1 : 0,
      notes: values.notes || null,
    };

    setCreatingRoom(true);
    try {
      const response = await axios.post(
        `${API_CONFIG.API_URL}/hotel/rooms`,
        payload
      );
      if (response.data.success) {
        message.success("Chambre creee.");
        setRoomModalOpen(false);
        roomForm.resetFields();
        fetchRooms();
      }
    } catch (error: any) {
      console.error("[HOTEL] Error creating room:", error);
      message.error(
        error.response?.data?.message || "Erreur lors de la creation de la chambre"
      );
    } finally {
      setCreatingRoom(false);
    }
  };

  const handleRoomStatusChange = (room: RoomRecord, nextStatus: RoomStatus) => {
    if (room.status === nextStatus) {
      return;
    }
    updateRoom(room.id, { status: nextStatus });
  };

  const handleHousekeepingChange = (
    room: RoomRecord,
    nextStatus: HousekeepingStatus
  ) => {
    if (room.housekeepingStatus === nextStatus) {
      return;
    }
    updateRoom(room.id, { housekeeping_status: nextStatus });
  };

  const columns: ColumnsType<RoomRecord> = [
    {
      title: "Chambre",
      dataIndex: "roomNumber",
      key: "roomNumber",
      render: (_value, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.roomNumber}</Text>
          <Text type="secondary">
            {(record.building || "Principal") + " - Etage " + (record.floor || "-")}
          </Text>
        </Space>
      ),
    },
    {
      title: "Type de chambre",
      dataIndex: "roomType",
      key: "roomType",
    },
    {
      title: "Propriete",
      dataIndex: "propertyName",
      key: "propertyName",
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      render: (value: RoomStatus, record) =>
        canEditRooms ? (
          <Select
            value={value}
            style={{ minWidth: 140 }}
            onChange={(next) => handleRoomStatusChange(record, next)}
            disabled={updatingRooms[record.id]}
            options={[
              { value: "available", label: statusLabels.available },
              { value: "occupied", label: statusLabels.occupied },
              { value: "out_of_order", label: statusLabels.out_of_order },
              { value: "out_of_service", label: statusLabels.out_of_service },
              { value: "maintenance", label: statusLabels.maintenance },
            ]}
          />
        ) : (
          <Tag color={statusColors[value]}>{statusLabels[value]}</Tag>
        ),
    },
    {
      title: "Menage",
      dataIndex: "housekeepingStatus",
      key: "housekeepingStatus",
      render: (value: HousekeepingStatus, record) =>
        canEditRooms ? (
          <Select
            value={value}
            style={{ minWidth: 150 }}
            onChange={(next) => handleHousekeepingChange(record, next)}
            disabled={updatingRooms[record.id]}
            options={[
              { value: "clean", label: housekeepingLabels.clean },
              { value: "dirty", label: housekeepingLabels.dirty },
              { value: "cleaning", label: housekeepingLabels.cleaning },
              { value: "inspected", label: housekeepingLabels.inspected },
              { value: "out_of_service", label: housekeepingLabels.out_of_service },
            ]}
          />
        ) : (
          <Badge
            status={housekeepingBadgeStatus[value]}
            text={housekeepingLabels[value]}
          />
        ),
    },
  ];

  const calendarColumns: ColumnsType<RateCalendarEntry> = [
    {
      title: "Date",
      dataIndex: "rateDate",
      key: "rateDate",
      render: (value: string) => dayjs(value).format("DD/MM/YYYY"),
    },
    {
      title: "Type de chambre",
      dataIndex: "roomTypeName",
      key: "roomTypeName",
    },
    {
      title: "Plan tarifaire",
      dataIndex: "ratePlanId",
      key: "ratePlanId",
      render: (value: number) => ratePlanMap.get(value)?.name || `Plan #${value}`,
    },
    {
      title: "Prix",
      dataIndex: "price",
      key: "price",
      align: "right",
      render: (value: number, record) =>
        formatCalendarPrice(value, record.ratePlanId),
    },
    {
      title: "Sejour min",
      dataIndex: "minStay",
      key: "minStay",
      render: (value: number | null | undefined) => value ?? "-",
    },
    {
      title: "Sejour max",
      dataIndex: "maxStay",
      key: "maxStay",
      render: (value: number | null | undefined) => value ?? "-",
    },
    {
      title: "Restrictions",
      key: "restrictions",
      render: (_value, record) => (
        <Space size={4} wrap>
          {record.stopSell ? (
            <Tag color="red">Fermeture</Tag>
          ) : (
            <Tag color="green">Ouvert</Tag>
          )}
          {record.closeToArrival ? <Tag color="orange">CTA</Tag> : null}
          {record.closeToDeparture ? <Tag color="orange">CTD</Tag> : null}
        </Space>
      ),
    },
  ];

  const handleRefreshCalendar = async () => {
    await fetchRateCalendar();
  };

  const handleRefreshBlocks = async () => {
    await fetchCalendarRoomBlocks();
  };

  if (requiresCompanySelection) {
    return (
      <Card>
        <Alert
          message="Selection requise"
          description="Selectionnez une entreprise pour acceder aux chambres."
          type="info"
          showIcon
        />
      </Card>
    );
  }

  return (
    <>
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={4}>
          <Card>
            <Statistic title="Total chambres" value={stats.total} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card>
            <Statistic title="Disponibles" value={stats.available} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card>
            <Statistic title="Occupees" value={stats.occupied} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card>
            <Statistic title="Hors service" value={stats.outOfService} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card>
            <Statistic title="Sales" value={stats.dirty} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card>
            <Statistic title="Occupation" value={`${stats.occupancyRate}%`} />
            <Progress percent={stats.occupancyRate} size="small" />
          </Card>
        </Col>
      </Row>

      {!hasProperties ? (
        <Alert
          message="Aucune propriete configuree"
          description="Creez une propriete dans l'onglet Configuration pour ajouter des chambres."
          type="info"
          showIcon
        />
      ) : !hasRoomTypes ? (
        <Alert
          message="Aucun type de chambre configure"
          description="Creez un type de chambre dans l'onglet Configuration pour ajouter des chambres."
          type="info"
          showIcon
        />
      ) : null}

      <Card title="Filtres">
        <Form layout="vertical">
          <Row gutter={[16, 16]}>
            <Col xs={24} md={6}>
              <Form.Item label="Propriete">
                <Select
                  value={filters.propertyId}
                  onChange={(value) =>
                    setFilters((prev) => ({ ...prev, propertyId: value }))
                  }
                  options={[
                    { value: "all", label: "Tous" },
                    ...properties.map((property) => ({
                      value: property.id,
                      label: property.name,
                    })),
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Type de chambre">
                <Select
                  value={filters.roomTypeId}
                  onChange={(value) =>
                    setFilters((prev) => ({ ...prev, roomTypeId: value }))
                  }
                  options={[
                    { value: "all", label: "Tous" },
                    ...roomTypes.map((roomType) => ({
                      value: roomType.id,
                      label: roomType.name,
                    })),
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Statut">
                <Select
                  value={filters.status}
                  onChange={(value) =>
                    setFilters((prev) => ({ ...prev, status: value }))
                  }
                  options={[
                    { value: "all", label: "Tous" },
                    { value: "available", label: statusLabels.available },
                    { value: "occupied", label: statusLabels.occupied },
                    { value: "out_of_order", label: statusLabels.out_of_order },
                    { value: "out_of_service", label: statusLabels.out_of_service },
                    { value: "maintenance", label: statusLabels.maintenance },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Menage">
                <Select
                  value={filters.housekeeping}
                  onChange={(value) =>
                    setFilters((prev) => ({ ...prev, housekeeping: value }))
                  }
                  options={[
                    { value: "all", label: "Tous" },
                    { value: "clean", label: housekeepingLabels.clean },
                    { value: "dirty", label: housekeepingLabels.dirty },
                    { value: "cleaning", label: housekeepingLabels.cleaning },
                    { value: "inspected", label: housekeepingLabels.inspected },
                    { value: "out_of_service", label: housekeepingLabels.out_of_service },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Recherche">
                <Input
                  placeholder="Chambre ou type"
                  value={filters.search}
                  onChange={(event) =>
                    setFilters((prev) => ({
                      ...prev,
                      search: event.target.value,
                    }))
                  }
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      <Card
        title="Calendrier disponibilites"
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefreshCalendar}
            loading={loadingCalendar}
          >
            Rafraichir calendrier
          </Button>
        }
      >
        <Form layout="vertical">
          <Row gutter={[16, 16]}>
            <Col xs={24} md={6}>
              <Form.Item label="Propriete">
                <Select
                  value={calendarFilters.propertyId}
                  onChange={(value) =>
                    setCalendarFilters((prev) => ({
                      ...prev,
                      propertyId: value,
                      roomTypeId: "all",
                      ratePlanId: "all",
                    }))
                  }
                  options={[
                    { value: "all", label: "Tous" },
                    ...properties.map((property) => ({
                      value: property.id,
                      label: property.name,
                    })),
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Plan tarifaire">
                <Select
                  value={calendarFilters.ratePlanId}
                  onChange={(value) =>
                    setCalendarFilters((prev) => ({
                      ...prev,
                      ratePlanId: value,
                    }))
                  }
                  options={[
                    { value: "all", label: "Tous" },
                    ...ratePlans.map((plan) => ({
                      value: plan.id,
                      label: plan.name,
                    })),
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Type de chambre">
                <Select
                  value={calendarFilters.roomTypeId}
                  onChange={(value) =>
                    setCalendarFilters((prev) => ({
                      ...prev,
                      roomTypeId: value,
                    }))
                  }
                  options={[
                    { value: "all", label: "Tous" },
                    ...calendarRoomTypes.map((roomType) => ({
                      value: roomType.id,
                      label: roomType.name,
                    })),
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Periode">
                <RangePicker
                  value={calendarFilters.dateRange ?? null}
                  onChange={(range) =>
                    setCalendarFilters((prev) => ({
                      ...prev,
                      dateRange:
                        range && range[0] && range[1]
                          ? [range[0], range[1]]
                          : null,
                    }))
                  }
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
        {rateCalendarEntries.length === 0 && !loadingCalendar ? (
          <Empty description="Aucune entree pour cette periode." />
        ) : (
          <Table
            columns={calendarColumns}
            dataSource={rateCalendarEntries}
            rowKey="id"
            loading={loadingCalendar}
            pagination={{ pageSize: 7 }}
          />
        )}
      </Card>

      <Card
        title="Blocages chambres (periode)"
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefreshBlocks}
            loading={loadingCalendarBlocks}
          >
            Rafraichir blocages
          </Button>
        }
      >
        <List
          loading={loadingCalendarBlocks}
          dataSource={calendarRoomBlocks}
          locale={{ emptyText: "Aucun blocage sur la periode." }}
          renderItem={(block) => (
            <List.Item>
              <List.Item.Meta
                title={`Chambre ${block.roomNumber}`}
                description={`${block.propertyName} - ${dayjs(block.startDate).format(
                  "DD/MM/YYYY"
                )} - ${dayjs(block.endDate).format("DD/MM/YYYY")}`}
              />
              <Space>
                <Tag color={block.status === "blocked" ? "red" : "default"}>
                  {block.status === "blocked" ? "Bloque" : block.status}
                </Tag>
                {block.reason ? <Text type="secondary">{block.reason}</Text> : null}
              </Space>
            </List.Item>
          )}
        />
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={16}>
          <Card
            title="Chambres"
            extra={
              <Space>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={fetchRooms}
                  loading={loadingRooms}
                >
                  Rafraichir
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={openCreateRoom}
                  disabled={!canCreateRooms || !hasProperties || !hasRoomTypes}
                >
                  Ajouter une chambre
                </Button>
              </Space>
            }
          >
            <Table
              columns={columns}
              dataSource={filteredRooms}
              rowKey="id"
              loading={loadingRooms}
              pagination={{ pageSize: 6 }}
            />
          </Card>
        </Col>
        <Col xs={24} xl={8}>
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <Card title="Disponibilite par type de chambre">
              <Table
                dataSource={availabilityByType}
                rowKey="roomType"
                pagination={false}
                size="small"
                columns={[
                  { title: "Type", dataIndex: "roomType", key: "roomType" },
                  { title: "Total", dataIndex: "total", key: "total" },
                  { title: "Disponibles", dataIndex: "available", key: "available" },
                  { title: "Occupees", dataIndex: "occupied", key: "occupied" },
                ]}
              />
            </Card>
          </Space>
        </Col>
      </Row>
      </Space>

      <Modal
        title="Ajouter une chambre"
        open={roomModalOpen}
        onCancel={() => setRoomModalOpen(false)}
        onOk={handleSaveRoom}
        confirmLoading={creatingRoom}
        okText="Enregistrer"
        cancelText="Annuler"
      >
        <Form layout="vertical" form={roomForm}>
          <Form.Item
            label="Propriete"
            name="propertyId"
            rules={[{ required: true, message: "Propriete requise" }]}
          >
            <Select
              placeholder="Selectionnez une propriete"
              disabled={filters.propertyId !== "all"}
              options={properties.map((property) => ({
                value: property.id,
                label: property.name,
              }))}
            />
          </Form.Item>

          <Form.Item
            label="Type de chambre"
            name="roomTypeId"
            rules={[{ required: true, message: "Type de chambre requis" }]}
          >
            <Select
              placeholder="Selectionnez un type"
              options={availableRoomTypes.map((roomType) => ({
                value: roomType.id,
                label: roomType.name,
              }))}
            />
          </Form.Item>

          <Form.Item
            label="Numero de chambre"
            name="roomNumber"
            rules={[{ required: true, message: "Numero requis" }]}
          >
            <Input placeholder="Ex: 101" />
          </Form.Item>

          <Form.Item label="Nom interne" name="name">
            <Input placeholder="Optionnel" />
          </Form.Item>

          <Form.Item
            label="Statut"
            name="status"
            rules={[{ required: true, message: "Statut requis" }]}
          >
            <Select
              options={[
                { value: "available", label: statusLabels.available },
                { value: "occupied", label: statusLabels.occupied },
                { value: "out_of_order", label: statusLabels.out_of_order },
                { value: "out_of_service", label: statusLabels.out_of_service },
                { value: "maintenance", label: statusLabels.maintenance },
              ]}
            />
          </Form.Item>

          <Form.Item
            label="Menage"
            name="housekeepingStatus"
            rules={[{ required: true, message: "Statut menage requis" }]}
          >
            <Select
              options={[
                { value: "clean", label: housekeepingLabels.clean },
                { value: "dirty", label: housekeepingLabels.dirty },
                { value: "cleaning", label: housekeepingLabels.cleaning },
                { value: "inspected", label: housekeepingLabels.inspected },
                {
                  value: "out_of_service",
                  label: housekeepingLabels.out_of_service,
                },
              ]}
            />
          </Form.Item>

          <Form.Item
            label="Actif"
            name="isActive"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item label="Notes" name="notes">
            <Input.TextArea rows={3} placeholder="Optionnel" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default RoomsAvailabilityTab;

